import React from 'react';
import moment from 'moment';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
} from 'recharts';

// ── Calm pacing visuals ──────────────────────────────────────────────────
// The new pacing view renders numbers in neutral ink and carries status
// through small, deliberate marks — a 6px status dot and a thin progress
// bar — in desaturated hues, instead of coloring the number text itself.
// Classic view keeps the legacy colored-text display and does not use
// these helpers.
export const PACING_TONES = {
  onTrack: '#5B9E71', // muted sage
  watch: '#D4A72C', // soft amber
  behind: '#C86B64', // muted terracotta (replaces alarm red)
  neutral: '#8c8c8c',
  ink: '#262626',
};

export const TREND_ARROWS = { rising: '↑', falling: '↓', steady: '→' };

// All-time on-pace ratio: delivered vs the pro-rata expected-to-date
// (goal × elapsed days ÷ total days). ~100 = on pace. Returns null when
// inputs are missing so callers can fall back to the legacy number; a
// campaign that has genuinely delivered nothing yields 0, not null.
export function getAllTimeOnPaceRatio(goal, delivered, daysIntoFlight, daysLeft) {
  const totalDays = (daysIntoFlight || 0) + (daysLeft || 0);
  const expected = totalDays > 0 ? (goal * daysIntoFlight) / totalDays : 0;
  if (!(expected > 0)) return null;
  if (delivered === null || delivered === undefined) return null;
  return (delivered / expected) * 100;
}

// Full-flight projection, the same formula the backend uses per segment:
// (rate × days after the anchor day + delivered through the anchor) ÷ goal.
// The backend only emits a today-anchored all-time projection, and today is
// a partial day until the pacing run after midnight, so the modal rebuilds
// it here from the all-time entry so the Yesterday/Today selector applies.
// Returns null when the all-time entry or its goal is missing so callers
// can fall back to the position ratio.
// `projectToDate` (YYYY-MM-DD) is a what-if end date: the full goal kept,
// the days recounted from the latest data date to that date. Without it the
// flight's own days-left applies.
export function getAllTimeProjection({
  allTime,
  pacingData,
  todayEntry,
  yesterday = true,
  isPercentage = false,
  projectToDate = null,
} = {}) {
  if (!allTime) return null;
  const goal = isPercentage ? allTime.totalBudget : allTime.totalImpressionGoal;
  if (!(goal > 0)) return null;
  const servedThroughToday = isPercentage ? allTime.budgetSpent : allTime.impressionsServed;
  if (servedThroughToday === null || servedThroughToday === undefined) return null;
  const todayAmount = isPercentage
    ? todayEntry?.budgetSpentToday ?? pacingData?.budgetSpentToday ?? 0
    : todayEntry?.impressionServedToday ?? pacingData?.impressionsToday ?? 0;
  const yesterdayAmount = isPercentage
    ? pacingData?.budgetSpentYesterday ?? 0
    : pacingData?.impressionsYesterday ?? 0;
  // allTime.daysLeft counts today; projecting from yesterday covers today
  // onward, projecting from today covers tomorrow onward.
  const daysLeft = Math.max(daysLeftToDate(projectToDate, pacingData?.lastDataDate) ?? allTime.daysLeft ?? 0, 0);
  const ratePerDay = yesterday ? yesterdayAmount : todayAmount;
  const delivered = yesterday ? Math.max(servedThroughToday - todayAmount, 0) : servedThroughToday;
  const daysAfter = yesterday ? daysLeft : Math.max(daysLeft - 1, 0);
  const pace = ((ratePerDay * daysAfter + delivered) / goal) * 100;
  const neededPerDay = daysAfter > 0 ? Math.max(goal - delivered, 0) / daysAfter : 0;
  return { pace, ratePerDay, neededPerDay, delivered, daysAfter, goal };
}

// Days from the latest data date through `toDate`, both inclusive (so the
// data date itself counts, like allTime.daysLeft). null when either date is
// missing or unparseable, so callers keep the flight's own days-left.
export function daysLeftToDate(toDate, latestDate) {
  if (!toDate || !latestDate) return null;
  const to = moment.utc(toDate);
  const latest = moment.utc(latestDate);
  if (!to.isValid() || !latest.isValid()) return null;
  return to.startOf('day').diff(latest.startOf('day'), 'days') + 1;
}

// The delivery rate the "Delivering" bar should show: impressions on the
// anchor day. dailyAvgImpressions is the planned daily average (goal ÷
// flight days), not delivery, so it is never a fallback here.
export function getDeliveryRatePerDay(pacingData, { yesterday = true, todayEntry } = {}) {
  if (yesterday) return pacingData?.impressionsYesterday ?? 0;
  return todayEntry?.impressionServedToday ?? pacingData?.impressionsToday ?? 0;
}

// Compact remaining-impressions format so the "Remaining:" line in the
// narrow pacing cell never wraps. Campaigns stay under 10M impressions, so
// two significant units (e.g. 6.79M / 678.9K / 9,876) always fit one line.
export function formatCompactRemaining(value) {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!isFinite(num)) return '—';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e4) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`;
}

// Tone for the all-time on-pace RATIO (~100 = on pace) — distinct from
// getPacingTone, whose gates encode projection / %-of-budget semantics.
// Applied to a ratio they invert: 60% of expected would read "on track".
// Over-delivering/overspending >120% is also "behind" (waste / overspend).
export function getAllTimeTone(ratio) {
  if (ratio === null || ratio === undefined) return PACING_TONES.neutral;
  if (ratio > 120) return PACING_TONES.behind;
  if (ratio >= 95) return PACING_TONES.onTrack;
  if (ratio >= 80) return PACING_TONES.watch;
  return PACING_TONES.behind;
}

// Append an alpha suffix to a 6-digit hex tone; non-hex colors (rgba,
// named) pass through unchanged so callers can't silently render black.
export const withAlpha = (color, alpha = '66') =>
  /^#[0-9a-fA-F]{6}$/.test(color) ? `${color}${alpha}` : color;

// Mirrors the legacy threshold logic (percentage vs impression pacing) but
// returns a calm tone. `gracePeriod` downgrades 'behind' to 'watch' while
// the flight is too young for a sub-threshold projection to be signal.
export function getPacingTone(pace, { isPercentage = false, gracePeriod = false } = {}) {
  if (pace === null || pace === undefined || pace <= 0) {
    return gracePeriod ? PACING_TONES.watch : PACING_TONES.behind;
  }
  if (isPercentage) {
    if (pace <= 100 && pace >= 50) return PACING_TONES.onTrack;
    if (pace >= 0 && pace < 50) return PACING_TONES.watch;
    return gracePeriod ? PACING_TONES.watch : PACING_TONES.behind;
  }
  if (pace >= 95) return PACING_TONES.onTrack;
  if (pace >= 91) return PACING_TONES.watch;
  return gracePeriod ? PACING_TONES.watch : PACING_TONES.behind;
}

// Bar maps 0–125% so a healthy 100% projection still shows headroom.
export const pacingBarFill = (pace, tooYoung) =>
  tooYoung ? 0 : Math.max(0, Math.min(125, pace || 0)) / 1.25;

// Shared flight-phase math for the table cell, card and order queue so the
// "Ramping" chip fires identically on every surface: only inside the
// unstable early window — the first min(5, ~20% of flight) days — and never
// on day 0 / missing data, where there is no rate to extrapolate from.
// Callers still gate on their own classic/ended flags. Accepts null
// (nullable GraphQL field) — renders a zeroed phase, never throws.
export function getFlightPhase(input) {
  const { daysIntoFlight = 0, daysLeft = 0 } = input || {};
  const into = daysIntoFlight || 0;
  const left = daysLeft || 0;
  const flightLength = into + left;
  const unstableDays = Math.max(2, Math.min(5, Math.round(flightLength * 0.2)));
  return {
    daysIntoFlight: into,
    daysLeft: left,
    flightLength,
    unstableDays,
    isRamping: into > 0 && into < unstableDays,
  };
}

export function PacingDot({ tone }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: tone,
        marginRight: 4,
        flexShrink: 0,
      }}
    />
  );
}

// Micro progress bar with a marker at 100% of goal (80% of the track,
// since the track spans 0–125%).
export function PacingBar({ pace, tooYoung, tone, height = 3, showMarker = true, style }) {
  const fill = pacingBarFill(pace, tooYoung);
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height,
        backgroundColor: '#f0f0f0',
        borderRadius: height / 2,
        ...style,
      }}
    >
      <div
        style={{
          width: `${fill}%`,
          height: '100%',
          backgroundColor: tone,
          borderRadius: height / 2,
          transition: 'width 0.15s',
        }}
      />
      {showMarker && (
        <div
          style={{
            position: 'absolute',
            top: -1,
            bottom: -1,
            left: '80%',
            width: 1,
            backgroundColor: '#bfbfbf',
          }}
        />
      )}
    </div>
  );
}

// Bullet-style delivery bar: fill = delivered-to-date as % of goal, gray
// tick = 100% of goal, colored marker = projected finish. Track spans
// 0–125% so a full delivery still shows headroom.
export function BulletBar({ deliveredPct, projectionPct, tone, height = 6 }) {
  // Same 0–125 → 0–100 mapping as pacingBarFill: clamp then divide by 1.25.
  const toTrack = (v) => Math.max(0, Math.min(125, v || 0)) / 1.25;
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height,
        backgroundColor: '#f0f0f0',
        borderRadius: height / 2,
      }}
    >
      <div
        style={{
          width: `${toTrack(deliveredPct)}%`,
          height: '100%',
          backgroundColor: tone,
          opacity: 0.45,
          borderRadius: height / 2,
          transition: 'width 0.15s',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: -1,
          bottom: -1,
          left: '80%',
          width: 1,
          backgroundColor: '#bfbfbf',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: -2,
          bottom: -2,
          left: `${toTrack(projectionPct)}%`,
          width: 2,
          backgroundColor: tone,
          borderRadius: 1,
        }}
        title="Projected finish"
      />
    </div>
  );
}

// Delivering-vs-needed rate comparison: the ratio between the two bars is
// the most actionable pacing fact (deliver more or extend the flight).
// `planned` (goal ÷ flight days) is optional context on its own line so it
// is never mistaken for the delivery rate.
export function RateBars({ delivering, needed, planned, tone, labelWidth = 62, valueWidth = 64 }) {
  const max = Math.max(delivering || 0, needed || 0, planned || 0, 1);
  const row = (label, value, color) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
      <span style={{ width: labelWidth, color: '#595959', flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 5, backgroundColor: '#f0f0f0', borderRadius: 3 }}>
        <div
          style={{
            width: `${((value || 0) / max) * 100}%`,
            height: '100%',
            backgroundColor: color,
            borderRadius: 3,
          }}
        />
      </div>
      <span
        style={{
          width: valueWidth,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          color: PACING_TONES.ink,
          flexShrink: 0,
        }}
      >
        {formatCompactRemaining(value || 0)}/d
      </span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {row('Delivering', delivering, tone || PACING_TONES.onTrack)}
      {row('Needed', needed, PACING_TONES.neutral)}
      {planned > 0 && row('Planned', planned, '#d9d9d9')}
    </div>
  );
}

// Compact budget donut: spend-to-date ring with the % in the hole.
export function BudgetDonut({ spentPct, size = 34, tone }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, spentPct || 0));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f0f0f0" strokeWidth={4} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone || PACING_TONES.onTrack}
          strokeWidth={4}
          strokeDasharray={`${(pct / 100) * c} ${c}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 9,
          fontWeight: 600,
          color: PACING_TONES.ink,
        }}
      >
        {Math.round(pct)}%
      </div>
    </div>
  );
}

// Cumulative delivered-vs-goal chart: the shaded wedge between the
// delivered area and the dashed goal line IS the pacing story; the
// dotted projection continues the delivered line to its projected finish.
// flightStart/flightEnd (YYYY-MM-DD) anchor the goal line to real days
// elapsed since the flight start — not array position, which shifts when
// byDate starts late or skips zero-delivery days.
export function CumulativePacingChart({
  byDate,
  impressionGoal,
  flightLength,
  pace,
  flightStart,
  flightEnd,
  tone = PACING_TONES.onTrack,
  height = 130,
}) {
  const sorted = [...(byDate || [])]
    .filter((d) => d && d.xAxis)
    .sort((a, b) => (a.xAxis < b.xAxis ? -1 : 1));
  if (sorted.length < 2 || !impressionGoal) return null;
  const perDay = impressionGoal / Math.max(flightLength || 1, 1);
  const toMs = (x) => {
    const t = new Date(`${String(x).slice(0, 10)}T00:00:00Z`).getTime();
    return Number.isNaN(t) ? null : t;
  };
  const startMs = flightStart ? toMs(flightStart) : null;
  // 1-based day index since flight start; falls back to array position
  // when dates aren't parseable or flightStart is unknown.
  const dayIndexOf = (x, i) => {
    const t = toMs(x);
    if (startMs && t !== null && t >= startMs) {
      return Math.round((t - startMs) / 86400000) + 1;
    }
    return i + 1;
  };
  let cum = 0;
  const pts = sorted.map((d, i) => {
    cum += d.impressions || 0;
    return {
      xAxis: d.xAxis,
      delivered: cum,
      goal: Math.round(perDay * dayIndexOf(d.xAxis, i)),
      projected: null,
    };
  });
  const last = pts[pts.length - 1];
  // Projection is optional (all-time view passes null — position, not
  // forecast). When shown, the terminal point sits at the real flight end
  // date with a dot, so even a one-point series renders visibly. If the
  // data already reaches the flight end, the final projection is placed
  // on the last data point instead of vanishing.
  const showProjection = pace !== null && pace !== undefined && pace > 0;
  if (showProjection) {
    const projectedFinal = Math.round((pace / 100) * impressionGoal);
    const endMs = flightEnd ? toMs(flightEnd) : null;
    const lastMs = toMs(last.xAxis);
    const endX = endMs && (!lastMs || endMs > lastMs) ? String(flightEnd).slice(0, 10) : null;
    if (endX) {
      last.projected = last.delivered;
      const endIdx = startMs && endMs ? Math.round((endMs - startMs) / 86400000) + 1 : flightLength;
      pts.push({
        xAxis: endX,
        delivered: null,
        goal: Math.round(perDay * endIdx),
        projected: projectedFinal,
      });
    } else {
      last.projected = projectedFinal;
    }
  }
  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <ResponsiveContainer width="100%" height={height} style={{ display: 'block', width: '100%' }}>
        <ComposedChart data={pts} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <XAxis
          dataKey="xAxis"
          tick={{ fontSize: 10, fill: '#8c8c8c' }}
          tickFormatter={(v) => String(v).slice(5)}
          minTickGap={24}
        />
        <YAxis hide domain={[0, 'auto']} />
        <RTooltip
          formatter={(value, name) => [formatCompactRemaining(value), name]}
          contentStyle={{ fontSize: 12 }}
        />
        <Area
          type="monotone"
          dataKey="delivered"
          name="Delivered"
          stroke={tone}
          strokeWidth={2}
          fill={tone}
          fillOpacity={0.12}
          dot={false}
          connectNulls={false}
        />
        <Area
          type="linear"
          dataKey="goal"
          name="Goal pace"
          stroke="#bfbfbf"
          strokeDasharray="4 3"
          strokeWidth={1.5}
          fill="none"
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="projected"
          name="Projected"
          stroke={tone}
          strokeDasharray="4 3"
          strokeWidth={1.5}
          dot={{ r: 2, fill: tone, strokeWidth: 0 }}
          connectNulls={false}
        />
      </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// Daily delivery bars for the recent window with a dashed daily-goal
// line. highlightFromEnd picks the emphasized bar: 0 = today, 1 =
// yesterday (mirrors the Y/T toggle). Faded bars are tone at 55 alpha.
export function DailyDeliveryChart({
  byDate,
  dailyGoal,
  highlightFromEnd = 0,
  tone = PACING_TONES.onTrack,
  days = 14,
  height = 110,
  valueKey = 'impressions',
}) {
  const sorted = [...(byDate || [])]
    .filter((d) => d && d.xAxis)
    .sort((a, b) => (a.xAxis < b.xAxis ? -1 : 1));
  if (sorted.length < 2) return null;
  const recent = sorted.slice(-days).map((d) => ({
    xAxis: String(d.xAxis).slice(5),
    value: d[valueKey] || 0,
  }));
  const highlightIdx = recent.length - 1 - highlightFromEnd;
  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <ResponsiveContainer width="100%" height={height} style={{ display: 'block', width: '100%' }}>
        <BarChart data={recent} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
          <XAxis dataKey="xAxis" tick={{ fontSize: 10, fill: '#8c8c8c' }} minTickGap={16} />
          <YAxis hide domain={[0, 'auto']} />
          <RTooltip
            formatter={(value) => [formatCompactRemaining(value), 'Delivered']}
            contentStyle={{ fontSize: 12 }}
          />
          {dailyGoal > 0 && (
            <ReferenceLine y={dailyGoal} stroke="#bfbfbf" strokeDasharray="4 3" />
          )}
          <Bar dataKey="value" radius={[2, 2, 0, 0]}>
            {recent.map((d, i) => (
              <Cell key={d.xAxis} fill={i === highlightIdx ? tone : withAlpha(tone)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
